import Controller, { inject as controller } from '@ember/controller';
import { action, set } from '@ember/object';
import { trackedObject } from '@ember/reactive/collections';
import { type Registry as Services, service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';
import type { Dayjs } from 'dayjs';
import {
  type Platform,
  PLATFORMS,
} from 'frontend-utils/utils/constants/constant-values';
import type MarketParityParityManagerController from 'lumen/controllers/market/parity/parity-manager';
import type { RouterServiceWithLegacyTransitionTo } from 'lumen/initializers/router-legacy-transition-to';
import {
  ISSUE_TYPE_TRANSLATIONS,
  ISSUE_TYPES,
  type IssueType,
} from 'lumen/models/parity-manager/issue';
import type { HotelAndMarketServiceWithMarket } from 'lumen/services/hotel-and-market';
import { PLATFORM_TRANSLATION_KEYS } from 'lumen/utils/constants/general-constants';
import { PLATFORM_PRETTY_NAMES } from 'lumen/utils/constants/pi-constant-values';
import getAPIErrorMessage from 'lumen/utils/get-api-error-message';

interface ManualIssueSteps {
  ATTACHMENTS: number;
  BOOKING_DETAILS?: number;
  CHANNEL_INFO: number;
  HOTEL_INFO: number;
  META_INFO?: number;
  OTHER_INFO: number;
}

const MANUAL_ISSUE_STEPS: Record<IssueType, ManualIssueSteps> = {
  [ISSUE_TYPES.MAJOR_OTA]: {
    HOTEL_INFO: 1,
    CHANNEL_INFO: 2,
    OTHER_INFO: 3,
    ATTACHMENTS: 4,
  },
  [ISSUE_TYPES.TEST_BOOKING]: {
    HOTEL_INFO: 1,
    CHANNEL_INFO: 2,
    BOOKING_DETAILS: 3,
    OTHER_INFO: 4,
    ATTACHMENTS: 5,
  },
  [ISSUE_TYPES.BAIT_AND_SWITCH]: {
    HOTEL_INFO: 1,
    META_INFO: 2,
    CHANNEL_INFO: 3,
    OTHER_INFO: 4,
    ATTACHMENTS: 5,
  },
};

interface IssueEntity {
  id: number | string;
}

interface IssueSubscription extends IssueEntity {
  brand_code: string;
  name: string;
  subscription_currency?: string;
}

interface PlatformOption {
  id: Platform;
  label: string;
}

interface UploadError {
  payload?: {
    errors?: Record<string, unknown>[];
  };
}

/**
 * Fields are filled in step by step by the add issue forms, which also set
 * extra `*_name`, `*_url` and `*_timestamp` fields for attachments
 */
export interface NewIssue {
  actual_channel_rate_screenshot?: File | null;
  additional_screenshot?: File | null;
  arrival_date: Dayjs | string | null;
  assignee?: IssueEntity;
  base_cancellation: boolean;
  base_platform: Platform;
  base_rate?: string;
  base_room_name?: string;
  base_screenshot?: File | null;
  booking_id?: string;
  booking_reservation_file?: File | null;
  booking_summary?: File | null;
  brand_final_checkout_page?: File | null;
  brand_rate_screenshot?: File | null;
  brand_screenshot?: File | null;
  cancel_before?: string | null;
  cancellation_policy?: File | null;
  cancellation_proof?: File | null;
  channel?: IssueEntity;
  channel_cancellation: boolean;
  channel_final_checkout_page?: File | null;
  channel_platform: Platform;
  channel_rate?: string;
  channel_room_name?: string;
  channel_screenshot?: File | null;
  comment?: string;
  displayed_channel_rate_screenshot?: File | null;
  extranet_screenshot?: File | null;
  guest_first_name?: string;
  guest_last_name?: string;
  los: number;
  meta_rate?: string;
  meta_rate_screenshot?: File | null;
  meta_room_name?: string;
  metasearch_channel?: IssueEntity;
  metasearch_screenshot?: File | null;
  pos: {
    id: string;
    name: string;
  };
  rate?: IssueEntity;
  rate_code?: string;
  root_cause?: IssueEntity;
  status?: IssueEntity;
  subscription?: IssueSubscription;
}

/**
 * FormData converts every non-Blob value to a string (null becomes "null"),
 * this keeps that conversion while satisfying the `string | Blob` typing
 */
function appendFormData(
  formData: FormData,
  name: string,
  value: Blob | string | number | boolean | null | undefined,
): void {
  formData.append(name, value instanceof Blob ? value : String(value));
}

export default class MarketParityParityManagerAddissueController extends Controller {
  @service declare ajax: Services['ajax'];
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithMarket;
  @service declare intl: Services['intl'];
  @service declare router: RouterServiceWithLegacyTransitionTo;
  @service declare toast: Services['toast'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @controller('market.parity.parity-manager')
  declare parentController: MarketParityParityManagerController;

  @tracked newIssue = this._createDefaultIssue();
  @tracked step = this.manualIssueSteps.HOTEL_INFO;
  @tracked uploadingIssue = false;

  get isBaitAndSwitchView(): boolean {
    return this.parentController?.isBaitAndSwitchView;
  }

  get isMajorOtaView(): boolean {
    return this.parentController?.isMajorOtaView;
  }

  get issueFormTitle(): string {
    return this.intl.t(
      ISSUE_TYPE_TRANSLATIONS[this.parentController.selectedMetric],
    );
  }

  get isTestBookingView(): boolean {
    return this.parentController?.isTestBookingView;
  }

  get manualIssueSteps(): ManualIssueSteps {
    return MANUAL_ISSUE_STEPS[this.parentController.selectedMetric];
  }

  // Preserve reference for isEqual check in PowerSelect code
  @cached
  get platformOptions(): PlatformOption[] {
    return [
      PLATFORMS.PLATFORM_DESKTOP,
      PLATFORMS.PLATFORM_MOBILE_WEBSITE,
      PLATFORMS.PLATFORM_MOBILE_APP,
    ].map((platform) => ({
      id: platform,
      label: this.intl.t(PLATFORM_TRANSLATION_KEYS[platform]),
    }));
  }

  get subscriptionCurrency(): string | undefined {
    const subscriptionCurrency =
      this.newIssue?.subscription?.subscription_currency;
    const marketCurrency = this.hotelAndMarketService.market?.currencyCode;
    // Until the currency code is fetched from the selected subscription, display the market currency
    return subscriptionCurrency || marketCurrency;
  }

  _createDefaultIssue(): NewIssue {
    const arrivalDate = this.hotelAndMarketService.getLocalTime();

    return trackedObject<NewIssue>({
      arrival_date: arrivalDate,
      base_cancellation: false,
      base_platform: PLATFORMS.PLATFORM_DESKTOP,
      channel_cancellation: false,
      channel_platform: PLATFORMS.PLATFORM_DESKTOP,
      los: 1,
      pos: {
        id: 'US',
        name: 'United States',
      },
    });
  }

  loadAdditionalInfoFormData(formData: FormData, newIssue: NewIssue): void {
    const statusId = newIssue.status?.id;
    const rootCauseId = newIssue.root_cause?.id;
    const assigneeId = newIssue.assignee?.id;

    if (assigneeId) {
      appendFormData(formData, 'assignee_id', assigneeId);
    }

    if (statusId) {
      appendFormData(formData, 'status', statusId);
    }

    if (rootCauseId) {
      appendFormData(formData, 'root_cause_id', rootCauseId);
    }

    appendFormData(formData, 'comment', newIssue.comment ?? '');

    if (!this.parentController.isBaitAndSwitchView) {
      appendFormData(formData, 'rate_plan', newIssue.rate_code ?? '');
    }
  }

  loadAttachmentFormData(formData: FormData, newIssue: NewIssue): void {
    if (this.isMajorOtaView) {
      appendFormData(formData, 'base_screenshot', newIssue.base_screenshot);
      appendFormData(
        formData,
        'channel_screenshot',
        newIssue.channel_screenshot,
      );
      appendFormData(
        formData,
        'extranet_screenshot',
        newIssue.extranet_screenshot || null,
      ); // with null meaning it's optional
      appendFormData(
        formData,
        'additional_screenshot',
        newIssue.additional_screenshot || null,
      );
    } else if (this.isTestBookingView) {
      appendFormData(formData, 'brand_screenshot', newIssue.brand_screenshot);
      appendFormData(
        formData,
        'brand_final_checkout_page',
        newIssue.brand_final_checkout_page || null,
      );
      appendFormData(
        formData,
        'channel_final_checkout_page',
        newIssue.channel_final_checkout_page,
      );
      appendFormData(
        formData,
        'metasearch_screenshot',
        newIssue.metasearch_screenshot || null,
      );
      appendFormData(
        formData,
        'booking_reservation_file',
        newIssue.booking_reservation_file,
      );
      appendFormData(
        formData,
        'proof_of_cancellation',
        newIssue.cancellation_proof || null,
      );
      appendFormData(
        formData,
        'cancellation_policy_brand_and_channel',
        newIssue.cancellation_policy || null,
      );
      appendFormData(
        formData,
        'booking_summary',
        newIssue.booking_summary || null,
      );
    } else if (this.isBaitAndSwitchView) {
      appendFormData(
        formData,
        'meta_rate_screenshot',
        newIssue.meta_rate_screenshot || null,
      );
      appendFormData(
        formData,
        'brand_rate_screenshot',
        newIssue.brand_rate_screenshot,
      );
      appendFormData(
        formData,
        'displayed_channel_rate_screenshot',
        newIssue.displayed_channel_rate_screenshot,
      );
      appendFormData(
        formData,
        'actual_channel_rate_screenshot',
        newIssue.actual_channel_rate_screenshot,
      );
    }
  }

  loadBookingDetailsFormData(formData: FormData, newIssue: NewIssue): void {
    appendFormData(formData, 'guest_first_name', newIssue.guest_first_name);
    appendFormData(formData, 'guest_last_name', newIssue.guest_last_name);
    appendFormData(formData, 'booking_id', newIssue.booking_id);
    appendFormData(formData, 'cancel_before', newIssue.cancel_before || '');
    // formData.append('payment_type', newIssue.payment_type?.id || '');
    // formData.append('credit_card', newIssue.credit_card_number || null);
  }

  loadChannelRateFormData(formData: FormData, newIssue: NewIssue): void {
    appendFormData(formData, 'channel_id', newIssue.channel!.id);
    appendFormData(formData, 'channel_rate', newIssue.channel_rate);
    appendFormData(
      formData,
      'channel_room_name',
      newIssue.channel_room_name ?? '',
    );
    appendFormData(
      formData,
      'channel_cancellation',
      newIssue.channel_cancellation,
    );

    if (this.isMajorOtaView) {
      appendFormData(formData, 'channel_platform', newIssue.channel_platform);
    }
  }

  loadHotelRateFormData(formData: FormData, newIssue: NewIssue): void {
    const arrivalDate = this.hotelAndMarketService.toLocalTimeWithFallback(
      // The default arrival date is a Dayjs object, which dayjs parses the same way as a date string
      newIssue.arrival_date as string | null,
    );

    // issue_type is a required param to be able to add an issue; it's included in the hotel rate because all current metrics contain this form.
    appendFormData(
      formData,
      'issue_type',
      this.parentController.selectedMetric,
    );
    appendFormData(formData, 'subscription_id', newIssue.subscription!.id);
    appendFormData(formData, 'rate_type', newIssue.rate!.id);
    appendFormData(formData, 'base_rate', newIssue.base_rate);
    appendFormData(formData, 'base_cancellation', newIssue.base_cancellation);
    appendFormData(formData, 'base_room_name', newIssue.base_room_name ?? '');
    appendFormData(formData, 'arrival_date', arrivalDate.format('YYYY-MM-DD')); // Send the date in the YYYY-MM-DD format
    appendFormData(formData, 'los', newIssue.los);

    if (this.isMajorOtaView) {
      appendFormData(formData, 'base_platform', newIssue.base_platform);
      appendFormData(formData, 'pos', newIssue.pos.id);
    }
  }

  loadMetaRateFormData(formData: FormData, newIssue: NewIssue): void {
    appendFormData(
      formData,
      'metasearch_channel',
      newIssue.metasearch_channel!.id,
    );
    appendFormData(formData, 'meta_rate', newIssue.meta_rate);
    appendFormData(formData, 'meta_room_name', newIssue.meta_room_name ?? '');
  }

  @action
  back(): void {
    this.step -= 1;
  }

  @action
  exitForm(): void {
    this.step = this.manualIssueSteps.HOTEL_INFO;
    this.newIssue = this._createDefaultIssue();
    this.router.legacyTransitionTo('market.parity.parity-manager');
  }

  @action
  next(): void {
    this.step += 1;
  }

  @action
  redirectOnClose(): void {
    this.exitForm();
  }

  @action
  saveIssue(): void {
    this.uploadingIssue = true;
    const formData = new FormData();
    const { newIssue } = this;

    // Hotel rate form
    this.loadHotelRateFormData(formData, newIssue);

    // Channel rate form
    this.loadChannelRateFormData(formData, newIssue);

    // Meta rate form
    if (this.isBaitAndSwitchView) {
      this.loadMetaRateFormData(formData, newIssue);
    }

    // Booking details form
    if (this.isTestBookingView) {
      this.loadBookingDetailsFormData(formData, newIssue);
    }

    // Other information form (optional)
    this.loadAdditionalInfoFormData(formData, newIssue);

    // Attachment form
    this.loadAttachmentFormData(formData, newIssue);

    // Save new issue. The attachment form's checkFileSize already rejects files over the size limit
    void this.ajax
      .request(
        `${this.ajax.host}/apigateway/v1/app/paritymanager/insert?market_id=${this.hotelAndMarketService.market.id}&subscription_id=${this.newIssue?.subscription?.id}`,
        {
          method: 'POST',
          contentType: false,
          processData: false,
          mimeType: 'multipart/form-data',
          data: formData,
        },
      )
      .then((/* response */) => {
        this.usageAnalytics.track('Issue Added', {
          eventTrigger: 'add-issue-button',
          los: newIssue.los,
          pos: newIssue.pos.id,
          platform: PLATFORM_PRETTY_NAMES[newIssue.channel_platform],
        });

        this.uploadingIssue = false;
        this.toast.success(
          this.intl.t('parity_manager.upload_success').toString(),
          {
            showIcon: true,
          },
        );
        this.exitForm();
        // Force refresh
        this.router.refresh('market.parity.parity-manager');
      })
      .catch((error: unknown) => {
        this.uploadingIssue = false;

        if (error) {
          (error as UploadError).payload?.errors?.forEach((e) => {
            this.errorTracking.addBreadcrumb({
              message: 'Error while uploading issue',
              data: e,
            });
          });
          this.errorTracking.captureException(error);
        }

        void this.alerting.showErrorAlert({
          text: getAPIErrorMessage(error, this.intl),
        });
      });
  }

  @action
  setIssueProperty(property: string, newValue: unknown): void {
    set(this, `newIssue.${property}`, newValue);
  }

  @action
  setIssuePropertyFromEvent(property: string, event: Event): void {
    this.setIssueProperty(property, (event.target as HTMLInputElement).value);
  }

  @action
  toggleValue(field: string, event: Event): void {
    this.setIssueProperty(field, (event.target as HTMLInputElement).checked);
  }
}
